import { useMemo } from "react"

import { useDisabledColumnIdSet } from "~/hooks/useDisabledColumns"
import { useExtraDataDisplayNameMap } from "~/hooks/useSku"
import { getExtraDataDisplayName } from "~/utils/dynamicTableUtils"

import {
  buildChooserColumns,
  createTitleResolver,
  getExtraDataSource,
  type ChooserColumnMeta,
  type TitleSources,
} from "./columnChooserModel"

/**
 * The chooser's columns for one table: `columnIds` minus the company's disabled columns and
 * `excludedIds`, labelled through {@link createTitleResolver} and grouped by `curated`.
 */
export function useChooserColumns({
  columnIds,
  curated,
  titles,
  otherGroup,
  excludedIds,
  isHiddenOther,
}: {
  columnIds: readonly string[]
  curated: readonly ChooserColumnMeta[]
  titles: TitleSources
  otherGroup?: string
  excludedIds?: readonly string[]
  isHiddenOther?: (id: string) => boolean
}) {
  const disabledColumnIds = useDisabledColumnIdSet()

  const offeredIds = useMemo(() => {
    const excluded = new Set(excludedIds)
    return columnIds.filter((id) => !excluded.has(id) && !disabledColumnIds.has(id))
  }, [columnIds, excludedIds, disabledColumnIds])

  const { data: extraDataDisplayNameMap } = useExtraDataDisplayNameMap(
    offeredIds.some((id) => getExtraDataSource(id)),
  )

  const { overrides, fallbacks, format } = titles
  return useMemo(
    () =>
      buildChooserColumns({
        columnIds: offeredIds,
        curated,
        resolveTitle: createTitleResolver({ overrides, fallbacks, format }),
        extraDataTitle: (field, table) =>
          getExtraDataDisplayName(field, table, extraDataDisplayNameMap),
        otherGroup,
        isHiddenOther,
      }),
    [
      offeredIds,
      curated,
      overrides,
      fallbacks,
      format,
      extraDataDisplayNameMap,
      otherGroup,
      isHiddenOther,
    ],
  )
}
